import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { isAddress } from "viem";
import { usePublicClient } from "wagmi";
import chronosArtNftAbi from "../../abi/ChronosArtNFT.json";
import { t } from "../../i18n/messages";

function cardClassName({ className, clickable }) {
  return [
    "nft-card",
    clickable ? "is-clickable" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");
}

function toText(value) {
  return String(value ?? "").trim();
}

function parseTextArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item))
      .filter(Boolean);
  }
  if (value == null) return [];
  const text = toText(value);
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parseTextArray(parsed);
  } catch {
    if (text.startsWith("{") && text.endsWith("}")) {
      return parseTextArray(
        text
          .slice(1, -1)
          .split(",")
          .map((item) => item.trim().replace(/^"|"$/g, "")),
      );
    }
    return [text];
  }
  return [];
}

function buildPreviewImages(image, galleryImages) {
  return Array.from(
    new Set(
      [toText(image), ...parseTextArray(galleryImages)]
        .map((item) => toText(item))
        .filter(Boolean),
    ),
  );
}

export default function NftCard({
  locale,
  image,
  galleryImages = [],
  title,
  tokenId,
  nftAddress = "",
  tokenName = "",
  resolveTokenName = false,
  price,
  priceLabel = "Price",
  subtitle = "",
  badge = "",
  badgeTone = "default",
  onClick,
  className = "",
  children = null,
}) {
  const publicClient = usePublicClient();
  const clickable = typeof onClick === "function";
  const tokenIdText = String(tokenId || "").trim();
  const nftAddressText = String(nftAddress || "").trim();
  const canResolveTokenName =
    Boolean(resolveTokenName) &&
    Boolean(publicClient) &&
    isAddress(nftAddressText) &&
    /^\d+$/.test(tokenIdText);

  const tokenNameQuery = useQuery({
    queryKey: [
      "nft-card",
      "token-name",
      nftAddressText.toLowerCase(),
      tokenIdText,
    ],
    queryFn: async () => {
      const result = await publicClient.readContract({
        address: nftAddressText,
        abi: chronosArtNftAbi,
        functionName: "tokenName",
        args: [BigInt(tokenIdText)],
      });
      return String(result || "").trim();
    },
    enabled: canResolveTokenName,
    staleTime: 60_000,
    retry: 1,
  });

  const resolvedTokenName = String(tokenName || tokenNameQuery.data || "").trim();
  const displayTitle = title || resolvedTokenName || `Token #${tokenId || "-"}`;
  const previewImages = useMemo(
    () => buildPreviewImages(image, galleryImages),
    [image, galleryImages],
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const mainImage = previewImages[0] || "";
  const hasPreview = previewImages.length > 0;
  const thumbnailImages = previewImages.slice(1, 5);

  const onKeyDown = (event) => {
    if (!clickable) return;
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onClick();
  };

  const openPreviewAt = (index, event) => {
    if (!hasPreview) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const safeIndex = Math.max(0, Math.min(index, previewImages.length - 1));
    setPreviewIndex(safeIndex);
    setPreviewOpen(true);
  };

  const closePreview = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setPreviewOpen(false);
  };

  useEffect(() => {
    if (!previewOpen) return undefined;

    const onWindowKeyDown = (event) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
        return;
      }
      if (event.key === "ArrowLeft") {
        if (previewImages.length > 1) {
          setPreviewIndex((prev) =>
            prev <= 0 ? previewImages.length - 1 : prev - 1,
          );
        }
        return;
      }
      if (event.key === "ArrowRight") {
        if (previewImages.length > 1) {
          setPreviewIndex((prev) =>
            prev >= previewImages.length - 1 ? 0 : prev + 1,
          );
        }
      }
    };

    window.addEventListener("keydown", onWindowKeyDown);
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown);
    };
  }, [previewOpen, previewImages.length]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [previewOpen]);

  useEffect(() => {
    if (previewIndex < previewImages.length) return;
    setPreviewIndex(0);
  }, [previewIndex, previewImages.length]);

  const previewImage = previewImages[previewIndex] || "";
  const previewLayer =
    previewOpen && previewImage ? (
      <div
        className="nft-card-preview-overlay"
        role="dialog"
        aria-modal="true"
        onClick={closePreview}
      >
        <div className="nft-card-preview-stage">
          <img src={previewImage} alt={displayTitle} />
        </div>
      </div>
    ) : null;

  return (
    <>
      <article
        className={cardClassName({ className, clickable })}
        onClick={clickable ? onClick : undefined}
        onKeyDown={onKeyDown}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        <div className="nft-card-media">
          {mainImage ? (
            <button
              type="button"
              className="nft-card-media-main"
              onClick={(event) => openPreviewAt(0, event)}
              aria-label={t(locale, "detailPreviewImage", "Preview image")}
            >
              <img src={mainImage} alt={displayTitle} loading="lazy" />
            </button>
          ) : (
            <div className="nft-card-media-empty">
              {t(locale, "detailNoImage", "No image")}
            </div>
          )}

          {thumbnailImages.length ? (
            <div className="nft-card-media-thumbs">
              {thumbnailImages.map((url, idx) => (
                <button
                  key={`${url}-${idx}`}
                  type="button"
                  className="nft-card-media-thumb"
                  onClick={(event) => openPreviewAt(idx + 1, event)}
                  aria-label={`${t(locale, "detailPreviewImage", "Preview image")} ${idx + 2}`}
                >
                  <img src={url} alt={`${displayTitle} #${idx + 2}`} loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="nft-card-body">
          <div className="nft-card-head">
            <strong className="nft-card-title">{displayTitle}</strong>
            {badge ? (
              <span className={`nft-card-badge tone-${badgeTone}`}>
                {badge}
              </span>
            ) : null}
          </div>

          <div className="nft-card-row">
            <span className="nft-card-label">{t(locale, "detailTokenId", "Token ID")}</span>
            <span className="nft-card-value">#{tokenId || "-"}</span>
          </div>

          {resolveTokenName ? (
            <div className="nft-card-row">
              <span className="nft-card-label">{t(locale, "detailTokenName", "Token Name")}</span>
              <span className="nft-card-value">{resolvedTokenName || "-"}</span>
            </div>
          ) : null}

          {price != null && price !== "" ? (
            <div className="nft-card-row">
              <span className="nft-card-label">{priceLabel}</span>
              <span className="nft-card-value">{price}</span>
            </div>
          ) : null}

          {subtitle ? <p className="nft-card-subtitle">{subtitle}</p> : null}
          {children ? <div className="nft-card-extra">{children}</div> : null}
        </div>
      </article>

      {previewLayer && typeof document !== "undefined"
        ? createPortal(previewLayer, document.body)
        : previewLayer}
    </>
  );
}
